/**
 * Price Manager Singleton
 *
 * Central manager for ALL card pricing operations across the frontend.
 * Handles fetching, caching, and mapping of card prices from backend API.
 *
 * Usage:
 *   import { priceManager } from '@/lib/priceManager';
 *   const prices = await priceManager.getPrices('one-piece');
 *   const price = await priceManager.getCardPrice('one-piece', 'OP01-001');
 */

import { SupportedGames } from "@/types/game";
import { createInitialStoreState } from "@/games";
import { CardPrices } from "@/models/tcgplayer-prices";
import aaDataJson from "@/data/one_piece/aa_data.json";
import riftboundAaDataJson from "@/data/riftbound/aa_data.json";
import {
  getCardId as getUACardId,
  getUAToTCGPlayerIdMap,
} from "@/lib/games/unionArena/getCardDataLocal";
import { getCardId as getRBCardId } from "@/lib/games/riftbound/getCardDataLocal";
import { getTCGPlayerProductId } from "@/lib/tcgplayer/getCardPrice";

const aaData = aaDataJson as AAData;
const riftboundAaData = riftboundAaDataJson as AAData;

type SrcType = "asia_en" | "en" | "tcgplayer";

interface AACardData {
  asia_en_id: string | null;
  card_number: string;
  card_sets: string[] | null;
  count: number;
  hide: boolean;
  id: string;
  name: string;
  set: string;
  src: SrcType;
  tags: string[];
  tcgplayer_id: string | null;
}

type AAData = {
  [key: string]: AACardData[];
};

type Mapping = {
  [key: string]: number;
};

class PriceManager {
  private static instance: PriceManager;

  // Price caches
  // Built from the game registry so a new game does not need an edit here.
  private priceCache: Record<SupportedGames, CardPrices | null> =
    createInitialStoreState<CardPrices | null>(null);

  // Fetch tracking
  private fetchPromises: Record<SupportedGames, Promise<CardPrices> | null> =
    createInitialStoreState<Promise<CardPrices> | null>(null);

  private lastFetchTime: Record<SupportedGames, number> =
    createInitialStoreState<number>(0);

  // Cache duration: 5 minutes
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  private constructor() {
    this.initializeIdMaps();
  }

  public static getInstance(): PriceManager {
    if (!PriceManager.instance) {
      PriceManager.instance = new PriceManager();
    }
    return PriceManager.instance;
  }

  /**
   * Initialize TCGPlayer ID mappings from AA data
   */
  private initializeIdMaps() {}

  /**
   * Get TCGPlayer product ID for a specific card variant
   * Returns the exact TCGPlayer ID for the given card ID
   */
  /**
   * Delegates to the shared implementation in lib/tcgplayer/getCardPrice.
   * This class used to carry a byte-equivalent copy, built from the same
   * aa_data sources, that could drift independently.
   */
  public getTCGPlayerProductId(
    game: SupportedGames,
    cardId: string,
  ): string | undefined {
    return getTCGPlayerProductId(game, cardId);
  }

  /**
   * Get all prices for a game (cached)
   * Returns cached prices if available and fresh, otherwise fetches from backend
   */
  public async getPrices(game: SupportedGames): Promise<CardPrices> {
    const now = Date.now();
    const cacheAge = now - this.lastFetchTime[game];

    // Return cached prices if they exist and are fresh
    if (this.priceCache[game] && cacheAge < this.CACHE_DURATION) {
      return this.priceCache[game]!;
    }

    // If already fetching, return the existing promise
    if (this.fetchPromises[game]) {
      return this.fetchPromises[game]!;
    }

    // Fetch new prices
    const fetchPromise = this.fetchPricesFromBackend(game);
    this.fetchPromises[game] = fetchPromise;

    try {
      const prices = await fetchPromise;
      this.priceCache[game] = prices;
      this.lastFetchTime[game] = now;
      return prices;
    } finally {
      this.fetchPromises[game] = null;
    }
  }

  /**
   * Get price for a specific card by its card ID
   * Automatically handles TCGPlayer product ID mapping
   * Returns low price if available, otherwise market price, otherwise null
   */
  public async getCardPrice(
    game: SupportedGames,
    cardId: string,
  ): Promise<number | null> {
    try {
      const prices = await this.getPrices(game);
      const productId = this.getTCGPlayerProductId(game, cardId);

      if (productId && prices[productId]) {
        const priceData = prices[productId];

        // Use low price if available
        if (priceData.l && priceData.l.trim() !== "") {
          const price = parseFloat(priceData.l);
          if (!isNaN(price)) return price;
        }

        // Fallback to market price
        if (priceData.m && priceData.m.trim() !== "") {
          const price = parseFloat(priceData.m);
          if (!isNaN(price)) return price;
        }
      }
      return null;
    } catch (error) {
      console.error(`Error getting price for ${cardId}:`, error);
      return null;
    }
  }

  /**
   * Get price for a specific card by its TCGPlayer product ID (direct lookup)
   * Returns low price if available, otherwise market price, otherwise null
   */
  public async getCardPriceByProductId(
    game: SupportedGames,
    productId: string,
  ): Promise<number | null> {
    try {
      const prices = await this.getPrices(game);
      if (prices[productId]) {
        const priceData = prices[productId];

        // Use low price if available
        if (priceData.l && priceData.l.trim() !== "") {
          const price = parseFloat(priceData.l);
          if (!isNaN(price)) return price;
        }

        // Fallback to market price
        if (priceData.m && priceData.m.trim() !== "") {
          const price = parseFloat(priceData.m);
          if (!isNaN(price)) return price;
        }
      }
      return null;
    } catch (error) {
      console.error(`Error getting price for product ${productId}:`, error);
      return null;
    }
  }

  /**
   * Calculate total price for a decklist
   * Format: "2xOP01-001;1xOP01-002;..."
   */
  public async getDecklistPrice(
    game: SupportedGames,
    decklist: string,
  ): Promise<number> {
    let price = 0;
    const cards = decklist.split(";");

    for (const card of cards) {
      const [count, id] = card.split("x");
      try {
        const curPrice = await this.getCardPrice(game, id);
        if (curPrice !== null) {
          price = price + parseInt(count) * curPrice;
        }
      } catch {
        continue;
      }
    }
    return price;
  }

  /**
   * Fetch prices from backend API
   */
  private async fetchPricesFromBackend(
    game: SupportedGames,
  ): Promise<CardPrices> {
    // Only fetch on client side
    if (typeof window === "undefined") {
      throw new Error("Price fetching only available on client side");
    }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL;

    if (!apiUrl) {
      throw new Error("NEXT_PUBLIC_API_URL not configured");
    }

    const response = await fetch(`${apiUrl}/api/prices?game=${game}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch prices: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Clear cached prices (useful for forcing a refresh)
   */
  public clearCache(game?: SupportedGames) {
    if (game) {
      this.priceCache[game] = null;
      this.lastFetchTime[game] = 0;
    } else {
      // Clear all caches
      this.priceCache = createInitialStoreState<CardPrices | null>(null);
      this.lastFetchTime = createInitialStoreState<number>(0);
    }
  }

  /**
   * Check if prices are cached and fresh
   */
  public isCached(game: SupportedGames): boolean {
    const now = Date.now();
    const cacheAge = now - this.lastFetchTime[game];
    return this.priceCache[game] !== null && cacheAge < this.CACHE_DURATION;
  }

  /**
   * Get cached prices synchronously (returns null if not cached)
   */
  public getCachedPrices(game: SupportedGames): CardPrices | null {
    return this.priceCache[game];
  }
}

// Export singleton instance
export const priceManager = PriceManager.getInstance();
